/**
 * "Select all" helpers — shared by PreferenceSelector and
 * SegmentMatrixSelector so the bulk-set logic lives in one place.
 */
import { MultiselectAnswer, PreferenceLevel, QuestionOption } from './types';

/**
 * The level shared by every option, or undefined if the options are mixed /
 * incomplete. Drives the highlighted state of the "Select all" buttons.
 */
export const uniformLevel = (
  answer: MultiselectAnswer,
  options: QuestionOption[]
): PreferenceLevel | undefined => {
  if (options.length === 0) return undefined;
  const first = answer[options[0].value];
  if (!first) return undefined;
  return options.every((o) => answer[o.value] === first) ? first : undefined;
};

/**
 * Whether applying `level` to every option would change or clear an existing
 * answer — i.e. some option already has a value that the bulk action would
 * overwrite. Used to gate a confirmation step on "Select all".
 */
export const wouldOverwrite = (
  answer: MultiselectAnswer,
  options: QuestionOption[],
  level: PreferenceLevel | undefined
): boolean =>
  options.some((o) => {
    const current = answer[o.value];
    return current !== undefined && current !== level;
  });

/**
 * A new answer map with every option set to `level` — or an empty map when
 * `level` is undefined (clears the section).
 */
export const applyToAll = (
  options: QuestionOption[],
  level: PreferenceLevel | undefined
): MultiselectAnswer => {
  if (level === undefined) return {};
  const next: MultiselectAnswer = {};
  options.forEach((o) => {
    next[o.value] = level;
  });
  return next;
};
